import React, { useRef, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from '@mui/material';
import {
    FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight,
    FaMagnifyingGlassPlus, FaMagnifyingGlassMinus, FaCrosshairs
} from "react-icons/fa6";
import './draggable.css'
const FixedBox = {
    position: 'absolute',
    top: 0, bottom: 0,
    left: 0, right: 0,
    margin: 'auto',
    zIndex: 1999,
    userSelect: "none",
}
const FlexBox = {
    cursor: 'pointer',
    display: 'flex',
    gap: 2, alignItems: 'center', justifyContent: 'center', '&hover': { opacity: 0.6 }
}
const warppBox = { position: 'relative', m: 0, width: 140, height: 40 }

const fixWH = { width: 40, height: 40 }
export default function NaviBox({ icons = {},
    gap = 1, onChange = (status) => { },
    pan_tilt_only = false,
    zoom_only = false, zoom = 1,
    loading = false,
    activeAction = null,
}) {
    const resolvedIcons = {
        move_up: icons?.move_up || icons?.top || <FaArrowUp size={16} color="white" />,
        move_down: icons?.move_down || icons?.bottom || <FaArrowDown size={16} color="white" />,
        prev: icons?.prev || icons?.left || <FaArrowLeft size={16} color="white" />,
        next: icons?.next || icons?.right || <FaArrowRight size={16} color="white" />,
        center: icons?.center || icons?.reset || <FaCrosshairs size={18} color="#ffcc33" />,
        zoom_in: icons?.zoom_in || <FaMagnifyingGlassPlus size={16} color="white" />,
        zoom_out: icons?.zoom_out || <FaMagnifyingGlassMinus size={16} color="white" />,
    };
    const [isExpanded, setIsExpanded] = useState(false);
    const [spinningBtn, setSpinningBtn] = useState(null);
    const closeTimeoutRef = useRef(null);
    const spinTimeoutRef = useRef(null);

    // Keep active spinning in sync with parent loading
    useEffect(() => {
        if (!loading && !spinningBtn) {
            // idle
        }
    }, [loading, spinningBtn]);

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
        };
    }, []);

    const handleMouseEnter = () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        setIsExpanded(true);
    };

    const handleMouseLeave = () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        // Delay closing by 1800ms so the user can comfortably move around without buttons vanishing
        closeTimeoutRef.current = setTimeout(() => {
            setIsExpanded(false);
        }, 1800);
    };

    const handleAction = (action) => {
        // 1. Immediately activate glowing spinning ring on clicked button
        setSpinningBtn(action);
        if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = setTimeout(() => {
            setSpinningBtn(null);
        }, 1500);

        // 2. Keep navigation pad open for 2.5s after clicking so user can see action & spinner
        setIsExpanded(true);
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(() => {
            setIsExpanded(false);
        }, 2500);

        // 3. Dispatch action to viewer
        onChange(action);
    };

    const isSpinning = (btnAction) => {
        // Local click immediate trigger
        if (spinningBtn) {
            if (btnAction === spinningBtn) return true;
            if (btnAction === "reset" && (spinningBtn === "center" || spinningBtn === "home")) return true;
        }
        // Parent loading / hardware PTZ movement trigger
        if (loading) {
            const current = activeAction;
            if (!current) return false;
            if (btnAction === current) return true;
            if (btnAction === "reset" && (current === "center" || current === "home" || current === "reset")) return true;
        }
        return false;
    };

    const renderBtn = (action, iconContent, extraClass = "", extraSx = {}) => {
        const spinning = isSpinning(action);
        return (
            <Box
                sx={{
                    ...fixWH,
                    position: 'relative',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                    overflow: 'visible',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    transform: spinning ? 'scale(1.04)' : 'scale(1)',
                    '&:active': { transform: 'scale(0.92)' },
                    ...extraSx
                }}
                className={extraClass}
                onClick={() => handleAction(action)}
            >
                {spinning && <div className="navibox-btn-spinning-ring" />}
                {iconContent}
            </Box>
        );
    };

    if (zoom_only) return (
        <Box className="icon-filled-black" sx={{ ...FlexBox, gap: gap, justifyContent: 'space-between', gap: 0, overflow: 'visible' }}>
            {renderBtn("zoom-in", resolvedIcons.zoom_in)}
            <Box sx={{ ...FlexBox, justifyContent: 'space-between', width: 1 }}>
                <Box className="zoom-line" sx={{ height: 1, width: 1, borderBottom: '1px solid white' }}></Box>
                <Box className="input-label" sx={{ color: 'white', fontSize: 16 }}>{zoom}x</Box>
                <Box className="zoom-line" sx={{ height: 1, width: 1, borderBottom: '1px solid white' }}></Box>
            </Box>
            {renderBtn("zoom-out", resolvedIcons.zoom_out)}
        </Box>
    );

    const isBoxOpen = Boolean(pan_tilt_only || isExpanded || loading);

    return (
        <Box
            className={`controll-containter icon-filled-black ${isBoxOpen ? 'is-expanded' : ''} ${loading ? 'is-loading' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                ...warppBox,
                height: pan_tilt_only ? 'auto' : 40,
                overflow: 'visible'
            }}>
            <Box sx={{ bottom: 0, position: 'absolute', zIndex: 1999, width: 1, overflow: 'visible' }}>
                <Box sx={{ ...FlexBox, gap: gap }} className={pan_tilt_only ? "" : "controlls"}>
                    {!pan_tilt_only && <Box sx={fixWH}></Box>}
                    {renderBtn("up", resolvedIcons.move_up)}
                    {!pan_tilt_only && renderBtn("zoom-in", resolvedIcons.zoom_in)}
                </Box>
                <Box sx={{ ...FlexBox, gap: gap, my: gap, overflow: 'visible' }}>
                    {renderBtn("left", resolvedIcons.prev, pan_tilt_only ? "" : "controlls")}
                    {renderBtn("reset", resolvedIcons.center, "icon-filled-path")}
                    {renderBtn("right", resolvedIcons.next, pan_tilt_only ? "" : "controlls")}
                </Box>
                <Box sx={{ ...FlexBox, gap: gap }} className={pan_tilt_only ? "" : "controlls"}>
                    {!pan_tilt_only && <Box sx={fixWH}></Box>}
                    {renderBtn("down", resolvedIcons.move_down)}
                    {!pan_tilt_only && renderBtn("zoom-out", resolvedIcons.zoom_out)}
                </Box>
            </Box>
        </Box>
    );
}